import type { AgentCard, AgentHealth, BudgetState, ProjectBudget, RunHost } from "@roomate/core";

/** Mission control home (docs/specs/mission-control.md §4.3): sorting and the URL-kept filters. Pure, so it is unit tested. */

export const MISSION_FILTER_KEYS = ["project", "host", "agent", "deliverable", "health", "tier"] as const;
export type MissionFilterKey = (typeof MISSION_FILTER_KEYS)[number];
export type MissionFilters = Partial<Record<MissionFilterKey, string>>;

/** The filters in a URL query; an absent or empty value is no filter. */
export function filtersFromParams(params: URLSearchParams): MissionFilters {
  const out: MissionFilters = {};
  for (const k of MISSION_FILTER_KEYS) {
    const v = params.get(k);
    if (v) out[k] = v;
  }
  return out;
}

/** The URL query with one filter set or cleared; params that are not ours are kept. */
export function withFilter(params: URLSearchParams, key: MissionFilterKey, value: string): URLSearchParams {
  const next = new URLSearchParams(params);
  if (value) next.set(key, value);
  else next.delete(key);
  return next;
}

/** The `GET /mission` path for a set of filters. */
export function missionPath(filters: MissionFilters): string {
  const q = new URLSearchParams();
  for (const k of MISSION_FILTER_KEYS) if (filters[k]) q.set(k, filters[k]!);
  const s = q.toString();
  return s ? `/mission?${s}` : "/mission";
}

const RANK: Record<AgentHealth, number> = { off_track: 0, needs_you: 1, stalled: 2, near_budget: 2, on_track: 2, idle: 2 };

/** `off_track` first, then `needs_you`, then the rest; the server's order is kept within a group (§4.3). */
export function sortCards<T extends Pick<AgentCard, "health">>(cards: readonly T[]): T[] {
  return cards
    .map((c, i) => ({ c, i }))
    .sort((a, b) => RANK[a.c.health] - RANK[b.c.health] || a.i - b.i)
    .map((x) => x.c);
}

export const HEALTH_LABEL: Record<AgentHealth, string> = {
  on_track: "On track",
  needs_you: "Needs you",
  stalled: "Stalled",
  near_budget: "Near budget",
  off_track: "Off track",
  idle: "Idle",
};

export const HOST_LABEL: Record<RunHost, string> = { node: "Node", "vercel-sandbox": "Vercel sandbox" };

/** "$0.42 / $5", or the cost alone when the agent has no cap. */
export function costLine(costUsd: number, cap: number | null): string {
  const m = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
  return cap === null ? m(costUsd) : `${m(costUsd)} / ${m(cap)}`;
}

/** A project's month against its budget (MC10b): "$8 of $10 this month · 80%". Null when the project has no budget. */
export function budgetLine(b: Pick<ProjectBudget, "spendUsd" | "monthlyBudgetUsd" | "state">): string | null {
  if (b.monthlyBudgetUsd === null) return null;
  const m = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
  // The server picks the state from the unrounded spend; keep the shown share inside that state's band so the two never disagree.
  let pct = Math.floor((b.spendUsd / b.monthlyBudgetUsd) * 100 + 1e-9);
  if (b.state === "exhausted") pct = Math.max(pct, 100);
  else if (b.state === "warning") pct = Math.min(99, Math.max(pct, 80));
  else if (b.state === "ok") pct = Math.min(pct, 79);
  return `${m(b.spendUsd)} of ${m(b.monthlyBudgetUsd)} this month · ${pct}%`;
}

/** The budget of one project from `today.budgets`, or null when it has none set. */
export function budgetFor(budgets: readonly ProjectBudget[] | undefined, projectId: string): ProjectBudget | null {
  const b = budgets?.find((x) => x.projectId === projectId);
  return b && b.monthlyBudgetUsd !== null ? b : null;
}

/** `warning` (from 80%) and `exhausted` (from 100%) are loud; `ok` stays quiet. */
export const BUDGET_TONE: Record<BudgetState, string> = {
  none: "text-ink-3",
  ok: "text-ink-3",
  warning: "rounded-full bg-signal-soft px-2 py-0.5 font-medium text-[color:var(--medium)]",
  exhausted: "rounded-full bg-high/10 px-2 py-0.5 font-medium text-high",
};
export const BUDGET_STATE_LABEL: Record<BudgetState, string> = { none: "", ok: "", warning: "Nearing budget", exhausted: "Budget used up" };

/** First non-empty line of agent output, without terminal colour codes. */
export function oneLine(text: string): string {
  return (
    text
      .replace(/\u001b\[[0-9;?]*[A-Za-z]/g, "")
      .split("\n")
      .find((l) => l.trim())
      ?.trim() ?? ""
  );
}

/** Viewers see cards but no answer or stop controls (§4.3, the rule the Run page uses). */
export function canAct(me: { kind: string; access?: string }): boolean {
  return me.kind === "human" && me.access !== "viewer";
}
